import { get, getNamedCookies, pageRulesStateToProps } from 'ctm-util-functions';
import {
    controlPayloadSelector,
    experimentIdSelector,
    formPayloadSelector,
    locationQuerySelector,
    quoteIdSelector,
    selectedProductIdSelector,
    transactionPayloadSelector,
    payloadResultsSelector,
    resultsConfigSelector,
    getDescribedResultsSet,
    productInfoSelector
} from 'ctm-selectors';

import { offerEventSelector, prefillPayloadSelector } from 'ctm-selectors/selectors/user';
import { configModel } from '../app_config_model';

/**
 * Get the AuthConfig state.
 * @param {Immutable.Map} state   - The application Redux store.
 * @returns {Object}         - The AuthConfig state.
 */
export const getAuthConfig = (state) => {
    // @todo: need to find a better way of linking the baseUrl to the auth config, this will do for now
    const pageSettings = configModel.getModels().pageSchemaModel.getSettings();
    const authConfig = pageSettings.api.auth;
    authConfig.baseUrl = state.getIn(['serverConfig', 'accountServiceBaseUrl']);
    return authConfig;
};

/**
 * @desc Get marketing params from query strings or cookies
 * @param {Immutable.Map} state    - The application Redux store
 * @returns {Object}        - Marketing request payload to be posted to /new API
 */
export const getMarketingRequestPayload = (state) => {
    const queries = locationQuerySelector(state).toJS();
    const experimentId = experimentIdSelector(state);
    const namedCookies = getNamedCookies();

    return {
        gaClientId: queries.gaclientid || _getGaClientIdFromCookies(namedCookies),
        cid: queries.cid || namedCookies.CampaignID,
        gclid: queries.gclid || namedCookies.gclid,
        utmSource: queries.utm_source || namedCookies.utm_source,
        utmCampaign: queries.utm_campaign || namedCookies.utm_campaign,
        utmMedium: queries.utm_medium || namedCookies.utm_medium,
        utmTerm: queries.utm_term || namedCookies.utm_term,
        utmContent: queries.utm_content || namedCookies.utm_content,
        campaignId: queries.campaign_id || namedCookies.campaign_id,
        experimentId
    };
};

/**
 * Get GA Client Id from Cookies by using Google's ga() api:
 * https://developers.google.com/analytics/devguides/collection/analyticsjs/cookies-user-id
 * @note Because we load GA async from GTM, ga is not available until GA script is fully downloaded, we need a fall back
 * to get _ga from cookies manually
 * @param {Object} namedCookies - key value pair cookie object
 * @returns {String|undefined} Extracted GaClientId from cookies
 * @private
 */
const _getGaClientIdFromCookies = (namedCookies) => {
    let gaClientId;

    // First try get gaClientId using ga api
    if (window.ga) {
        window.ga((tracker) => {
            gaClientId = tracker.get('clientId');
        });
    }

    // if gaClientId is not set, try get from namedCookies
    if (!gaClientId) {
        gaClientId = namedCookies._ga;
        if (gaClientId) {
            const tempArray = gaClientId.split('.');
            if (tempArray.length > 1) {
                const partB = tempArray.pop();
                const partA = tempArray.pop();
                gaClientId = `${partA}.${partB}`;
            }
        }
    }
    return gaClientId;
};

/**
 * Get the JourneyPayload.
 * @param {Immutable.Map} state     - The application Redux store.
 * @returns {Object}          - the payload object.
 */
export const getJourneyPayload = (state) => {
    const transaction = transactionPayloadSelector(state);
    const form = formPayloadSelector(state);
    const control = controlPayloadSelector(state);
    const prefill = prefillPayloadSelector(state);
    return { transaction, form, control, prefill };
};

export const getAppState = (state) => {
    return state;
};

export const getOfferDetails = (state) => {
    const offerDetails = offerEventSelector(state);
    return { offers: offerDetails };
};

/**
 * Get the ProductPayload.
 * @param {Immutable.Map} state      - The application Redux store.
 * @returns {Object}          - The payload object.
 */
export const getProductPayload = (state) => {
    const transaction = transactionPayloadSelector(state);
    // todo: this needs to be fixed to properly handle when transaction is false.
    const brandCode = transaction.get('brandCode');
    const transactionId = transaction.get('journeyId');
    const productId = selectedProductIdSelector(state);
    return { brandCode, transactionId, payload: { productId } };
};

const getSelectedProductInfo = (state, productId) => {
    const quotes = getDescribedResultsSet(payloadResultsSelector(state), resultsConfigSelector(state));
    const selectedProduct = quotes.filter((product) => product.getIn(['productMeta', 'id']) === productId).get(0);
    if (selectedProduct) {
        const partnerReference = selectedProduct.getIn(['providerMeta', 'quoteNumber']);
        const partnerBrand = selectedProduct.getIn(['providerMeta', 'code']);
        return {
            partnerReference, partnerBrand
        };
    }
    const productInfo = productInfoSelector(state);
    const partnerBrand = productInfo.getIn(['product', 'providerCode']);
    const partnerReference = productInfo.getIn(['product', 'quoteReference']);
    return { partnerBrand, partnerReference };
};

/**
 * Get lead payload from state
 * @param {Immutable.Map} state - The application Redux store.
 * @param {Object} action - An API action generated via apiAction().
 * @returns {Object} - Payload to send a lead
 */
export const getLeadPayload = (state, action) => {
    const transaction = transactionPayloadSelector(state);

    // Mandatory params
    const eventType = action.type;
    const brand = transaction.get('brandCode');
    const verticalType = transaction.get('vertical');
    const journeyId = transaction.get('journeyId');
    // for handover the quoteId isn't in the store so we need to retrieve it from the url
    const quoteId = quoteIdSelector(state) || locationQuerySelector(state).get('quote_id') || '';
    // Get productId from requestPayload, the fallBack is selectedProductId, e.g. for more_info
    const productId = get(action, 'requestPayload.productId', selectedProductIdSelector(state)) || '';

    // Get the Selected Product for Provider (Partner) code (brand) and quoteNumber (reference)
    const { partnerReference, partnerBrand } = getSelectedProductInfo(state, productId);

    // Optional params
    const contactName = state.getIn(['vertical', 'callMeBack', 'form', 'name']) || '';
    const useOtherNumber = state.getIn(['vertical', 'callMeBack', 'form', 'helpers', 'useOtherNumber']) === 'Y';
    let contactNumber;
    // if useOtherNumber is set, prefer the (landline) phone over the mobile
    if (useOtherNumber) {
        contactNumber = state.getIn(['vertical', 'callMeBack', 'form', 'phone']) || state.getIn(['vertical', 'callMeBack', 'form', 'mobile']) || '';
    } else {
        contactNumber = state.getIn(['vertical', 'callMeBack', 'form', 'mobile']) || state.getIn(['vertical', 'callMeBack', 'form', 'phone']) || '';
    }
    return { eventType, brand, verticalType, journeyId, params: { productId, partnerReference, partnerBrand, quoteId, contactName, contactNumber } };
};

/**
 * Get the validation rules for a specified page
 * @param {Immutable.Map} state       - The application Redux store.
 * @param  {...[type]} args     - Extra args passed - in this case pageName.
 * @returns {Object}            - The set of validation rules for the specified page.
 */
export const getPageRules = (state, ...args) => {
    return pageRulesStateToProps(...args, 'validation')(state).rules;
};
